'use client';

import { Button } from '@/components/button';
import { resolvePersonalizedBlocks } from '@/helper/personalization/resolvePersonalizedBlocks';
import { getUserReplacements } from '@/helper/replacements';
import { isStoryblokEditorContext } from '@/helper/storyblok/isStoryblokEditorContext';
import { useIsMounted } from '@/hooks/useIsMounted';
import { useEffectiveUserSegmentIds } from '@/providers/PersonalizationProvider';
import { useUserStore } from '@/providers/UserStoreProvider';
import type { ComponentPersonalizedContentStoryblok } from '@/types/component-types-sb';
import { StoryblokComponent, storyblokEditable } from '@storyblok/react';
import { useMemo, useState } from 'react';

import classes from './PersonalizedContent.module.scss';
import PersonalizedContentSkeletons from './PersonalizedContentSkeletons';

const PersonalizedContent: React.FC<{ blok: ComponentPersonalizedContentStoryblok }> = ({ blok }) => {
  const isMounted = useIsMounted();
  const effectiveSegmentIds = useEffectiveUserSegmentIds();
  const { hydrated, loggingIn, name, email } = useUserStore((state) => ({
    hydrated: state.hydrated,
    loggingIn: state.loggingIn,
    name: state.name,
    email: state.email,
  }));
  const [showSkeletonSimulation, setShowSkeletonSimulation] = useState(false);

  const userReplacements = useMemo(() => getUserReplacements({ name, email }), [name, email]);
  const showSkeletonSimulationSwitch = isStoryblokEditorContext({ includeDevelopment: true });
  const { blocks, isPersonalized } = useMemo(
    () => resolvePersonalizedBlocks(blok, effectiveSegmentIds),
    [blok, effectiveSegmentIds]
  );

  // CDP (später): zusätzlich auf userSegmentsReady warten, wenn Segmente asynchron aus CDP kommen.
  const isLoading = !isMounted || !hydrated || loggingIn;

  if (isLoading || showSkeletonSimulation) {
    return (
      <div
        className={classes.PersonalizedContent}
        id={blok.anchor || undefined}
        data-testid="personalized-content-skeleton"
        {...storyblokEditable(blok)}
      >
        {showSkeletonSimulationSwitch ? (
          <Button
            className={classes.toggleSkeletonSimulation}
            onClick={() => setShowSkeletonSimulation(!showSkeletonSimulation)}
          >
            Toggle Skeleton Simulation
          </Button>
        ) : null}
        <PersonalizedContentSkeletons bloks={blok.default_content} />
      </div>
    );
  }

  return (
    <div className={classes.PersonalizedContent} id={blok.anchor || undefined} {...storyblokEditable(blok)}>
      {showSkeletonSimulationSwitch ? (
        <Button
          className={classes.toggleSkeletonSimulation}
          onClick={() => setShowSkeletonSimulation(!showSkeletonSimulation)}
        >
          Toggle Skeleton Simulation
        </Button>
      ) : null}
      {blocks.map((nestedBlok) => (
        <StoryblokComponent
          blok={nestedBlok}
          key={nestedBlok._uid}
          promotionTracking={{ isPersonalized }}
          replace={userReplacements}
        />
      ))}
    </div>
  );
};

export default PersonalizedContent;
